import { useState, useMemo, useCallback, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import {
  useGetV1Series,
  useGetV1SitesIdExplorerTree,
  getGetV1SeriesQueryOptions,
  getGetV1SeriesQueryKey,
  type GetV1SeriesParams,
  type getV1SeriesResponse,
} from "@solarkey/api-client";
import { EmptyState, ErrorState, Skeleton } from "@solarkey/ui";
import { TimeSeriesTile, UPlotChart, chartTheme, clampLiveDayWindow, type UPlotChartProps } from "@solarkey/charts";
import { PageHeader } from "../shell/PageHeader";
import { timeAxisValues } from "@solarkey/charts";

/* The device tree and its parameters are the SIGNAL MAP, read from
   GET /v1/sites/{id}/explorer-tree — never a screen constant (wireframe §04
   annotation 3). The endpoint lists only the devices the registry knows and only
   the signals those devices actually report, so every `signal` this screen hands
   to /v1/series is a seeded metric code and can never 422 into a silent chart. */

interface DeviceParam {
  signal: string;
  label: string;
  unit: string;
}

interface DeviceEntry {
  id: string;
  label: string;
  model?: string;
}

interface DeviceGroup {
  key: string;
  classLabel: string;
  count: number;
  devices: DeviceEntry[];
  params: DeviceParam[];
}

/* Only the grains the series SQL actually serves: `1h` and `1d` switch the source
   continuous aggregate, everything else reads the 5-minute aggregate. A "raw" or
   "15 min" chip would hand back 5-minute data under another name, so it is not
   offered (the endpoint serves aggregates only, never raw rows). */
type Resolution = "5min" | "1h" | "1d";
type CompareMode = "one_device_many_params" | "one_param_many_devices";
type ViewStyle = "stack" | "overlay_normalised" | "overlay_multi_axis";
type DisplayMode = "chart" | "table";

const RESOLUTIONS: { id: Resolution; label: string }[] = [
  { id: "5min", label: "5 min" },
  { id: "1h", label: "hourly" },
  { id: "1d", label: "daily" },
];

const MAX_PARAMS = 6;

const LINE_DASHES: (number[] | undefined)[] = [
  undefined, [6, 4], [12, 4], [2, 3], [8, 3, 2, 3],
];
const LINE_COLORS = [
  "var(--sk-chart-selected)",
  "var(--sk-chart-measured)",
  "var(--sk-chart-reference)",
  "var(--sk-chart-peer)",
  "var(--sk-chart-escapee-high)",
  "var(--sk-chart-escapee-low)",
];
const AXIS_LETTERS = ["A", "B", "C"];

const today = new Date();
const dayStart = new Date(today);
dayStart.setHours(0, 0, 0, 0);

/* The generation window every INTRADAY chart is framed to. The series is fetched
   from local midnight, so without this each plot auto-ranges across the night as
   well and six dead hours eat a third of the width before the plant wakes up —
   which is what the 12am-to-4pm axis was showing. P4's x axis runs 06 to 18; 19
   closes the last hour.

   Daily resolution spans many days, so it is left to auto-range: clamping it to
   hours of one day would hide every point. */
const DAY_FIRST_HOUR = 6;
const DAY_LAST_HOUR = 19;

function generationWindow(
  resolution: Resolution, times: number[],
): [number, number] | undefined {
  if (resolution === "1d") return undefined;
  const firstTs = times[0];
  if (firstTs === undefined) return undefined;
  // Unix SECONDS throughout this screen, and the window is taken from the first
  // sample's own date rather than the clock, so a past day frames the same way.
  const d = new Date(firstTs * 1000);
  const at = (h: number): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0, 0, 0).getTime() / 1000;
  // Frame 06:00–19:00, but pull the right edge in to where the data actually ends
  // on a live/partial day, so today's curve is not stranded in the left third
  // (UX-REV-002 2.1).
  return clampLiveDayWindow(at(DAY_FIRST_HOUR), at(DAY_LAST_HOUR), times);
}

function seriesParams(
  site: string, device: string, signal: string, resolution: Resolution,
): GetV1SeriesParams {
  return {
    site,
    device,
    signal,
    from: dayStart.toISOString(),
    to: today.toISOString(),
    grain: resolution,
  };
}

const RESOLUTION_IDS = new Set<string>(RESOLUTIONS.map((r) => r.id));

/** The view encoded in the current URL, if any. Validated against the tree before
 *  it is used, so a stale link can never select a device that does not exist. */
function readViewFromUrl(): {
  device: string | null;
  params: string[] | null;
  picks: { device: string; signal: string }[] | null;
  resolution: Resolution | null;
  compare: CompareMode | null;
  view: ViewStyle | null;
} {
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(window.location.search);
  } catch {
    return { device: null, params: null, picks: null, resolution: null, compare: null, view: null };
  }
  const res = q.get("res");
  const compare = q.get("compare");
  const view = q.get("view");
  const params = q.get("params");
  const paramList = params ? params.split(",").filter(Boolean) : null;
  const device = q.get("device");
  // Cross-class selection: `picks=device:signal,device:signal` (UX-REV-002 2.2).
  // Falls back to the legacy single-device `device`+`params` so old links still open.
  const picksRaw = q.get("picks");
  const picks = picksRaw
    ? picksRaw
        .split(",")
        .map((s) => s.split(":"))
        .filter((a): a is [string, string] => a.length === 2 && !!a[0] && !!a[1])
        .map(([d, sig]) => ({ device: d, signal: sig }))
    : device && paramList
      ? paramList.map((sig) => ({ device, signal: sig }))
      : null;
  return {
    device,
    params: paramList,
    picks: picks && picks.length ? picks : null,
    resolution: res && RESOLUTION_IDS.has(res) ? (res as Resolution) : null,
    compare:
      compare === "one_device_many_params" || compare === "one_param_many_devices"
        ? compare
        : null,
    view:
      view === "stack" || view === "overlay_normalised" || view === "overlay_multi_axis"
        ? view
        : null,
  };
}

export function ExplorerView({ site }: { site: string }): ReactNode {
  const treeQuery = useGetV1SitesIdExplorerTree(site);
  const queryClient = useQueryClient();
  const initial = useMemo(readViewFromUrl, []);

  const [selectedDevice, setSelectedDevice] = useState<string | null>(initial.device);
  // The charted series, as (device, signal) pairs that may span device classes
  // (UX-REV-002 2.2). `null` = the default (the focused device's first few params).
  const [picks, setPicks] = useState<{ device: string; signal: string }[] | null>(initial.picks);
  const [resolution, setResolution] = useState<Resolution>(initial.resolution ?? "5min");
  const [compareMode, setCompareMode] = useState<CompareMode>(
    initial.compare ?? "one_device_many_params",
  );
  const [viewStyle, setViewStyle] = useState<ViewStyle>(initial.view ?? "stack");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("chart");
  const [searchQuery, setSearchQuery] = useState("");
  const [exportNote, setExportNote] = useState<string | null>(null);

  const groups: DeviceGroup[] = useMemo(() => {
    if (!treeQuery.data || treeQuery.data.status !== 200) return [];
    return (treeQuery.data.data.data.groups ?? []).map((g, i) => ({
      key: g.device_class ?? g.classLabel ?? `group-${i}`,
      classLabel: g.classLabel ?? g.device_class ?? "Devices",
      count: g.count ?? (g.devices ?? []).length,
      devices: (g.devices ?? [])
        .filter((d): d is { id: string; label?: string; model?: string | null } =>
          typeof d.id === "string" && d.id.length > 0)
        .map((d) => ({ id: d.id, label: d.label ?? d.id, ...(d.model ? { model: d.model } : {}) })),
      params: (g.params ?? [])
        .filter((p): p is { signal: string; label?: string; unit?: string } =>
          typeof p.signal === "string" && p.signal.length > 0)
        .map((p) => ({ signal: p.signal, label: p.label ?? p.signal, unit: p.unit ?? "" })),
    }));
  }, [treeQuery.data]);

  // The effective selection: what the person picked when it still exists in the
  // tree, otherwise the first device the site actually reports on.
  const activeGroup =
    groups.find((g) => g.devices.some((d) => d.id === selectedDevice)) ??
    groups.find((g) => g.devices.length > 0 && g.params.length > 0) ??
    groups.find((g) => g.devices.length > 0);
  const activeDevice =
    activeGroup?.devices.find((d) => d.id === selectedDevice) ?? activeGroup?.devices[0];

  // device id -> its group, so a pick's parameter metadata (label, unit) resolves
  // whatever class the device belongs to (an irradiance pick on a weather station
  // alongside power picks on an inverter).
  const deviceGroup = useMemo(() => {
    const m = new Map<string, DeviceGroup>();
    for (const g of groups) for (const d of g.devices) m.set(d.id, g);
    return m;
  }, [groups]);

  const paramMeta = useCallback(
    (device: string, signal: string): DeviceParam =>
      deviceGroup.get(device)?.params.find((p) => p.signal === signal) ??
      { signal, label: signal, unit: "" },
    [deviceGroup],
  );

  // The default selection, before the viewer picks anything: the focused device's
  // first few parameters, as (device, signal) pairs.
  const defaultPicks = useMemo<{ device: string; signal: string }[]>(
    () =>
      activeDevice && activeGroup
        ? activeGroup.params.slice(0, 3).map((p) => ({ device: activeDevice.id, signal: p.signal }))
        : [],
    [activeDevice, activeGroup],
  );

  const effectivePicks = useMemo(
    () => (picks ?? defaultPicks).slice(0, MAX_PARAMS),
    [picks, defaultPicks],
  );

  const pickedKeys = useMemo(
    () => new Set(effectivePicks.map((p) => `${p.device}:${p.signal}`)),
    [effectivePicks],
  );

  const togglePick = useCallback(
    (device: string, signal: string) => {
      setPicks((prev) => {
        const base = prev ?? defaultPicks;
        const i = base.findIndex((p) => p.device === device && p.signal === signal);
        if (i >= 0) return base.filter((_, j) => j !== i);
        if (base.length >= MAX_PARAMS) return base;  // cap reached: ignore (UX: show count)
        return [...base, { device, signal }];
      });
    },
    [defaultPicks],
  );

  const deviceId = activeDevice?.id ?? "";
  const tiles = useMemo(
    () => effectivePicks.map((pk) => ({ device: pk.device, param: paramMeta(pk.device, pk.signal) })),
    [effectivePicks, paramMeta],
  );

  /** Export the series values that are on screen — the actual points, not a
   *  header row. Nothing is invented: a series that has not loaded is skipped and
   *  the person is told how many were written. */
  const handleExportCsv = useCallback(() => {
    const lines: string[] = ["timestamp,device,signal,unit,value,state"];
    let written = 0;
    for (const t of tiles) {
      const cached = queryClient.getQueryData<getV1SeriesResponse>(
        getGetV1SeriesQueryKey(seriesParams(site, t.device, t.param.signal, resolution)),
      );
      if (!cached || cached.status !== 200) continue;
      for (const p of cached.data.data.points ?? []) {
        lines.push(
          [
            p.ts,
            t.device,
            t.param.signal,
            t.param.unit,
            p.value ?? "",
            p.state ?? "",
          ].join(","),
        );
        written += 1;
      }
    }
    if (written === 0) {
      setExportNote("nothing to export — the selected series have not loaded yet");
      return;
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `explorer_${site}_${deviceId}_${resolution}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportNote(`${written.toLocaleString()} rows exported`);
  }, [tiles, site, deviceId, resolution, queryClient]);

  /** Copy a link that this screen reads back on load (see readViewFromUrl). */
  const handleCopyLink = useCallback(() => {
    if (!deviceId) return;
    const params = new URLSearchParams({
      device: deviceId,
      picks: effectivePicks.map((p) => `${p.device}:${p.signal}`).join(","),
      res: resolution,
      compare: compareMode,
      view: viewStyle,
    });
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    void navigator.clipboard
      .writeText(url)
      .then(() => setExportNote("link copied"))
      .catch(() => setExportNote("the browser refused clipboard access"));
  }, [deviceId, effectivePicks, resolution, compareMode, viewStyle]);

  if (treeQuery.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }} aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="tile" />
      </div>
    );
  }

  if (treeQuery.isError || !treeQuery.data || treeQuery.data.status !== 200) {
    return (
      <ErrorState
        title="Device tree unavailable"
        message="The site's device and parameter map could not be read, so there is nothing to explore."
        onRetry={() => void treeQuery.refetch()}
      />
    );
  }

  if (groups.length === 0 || !activeGroup || !activeDevice) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <PageHeader title="Explorer" scope={`Site ${site}`} showTimeControl={false} />
        <EmptyState
          variant="no_data"
          title="No instrumented devices on this site"
          message="The registry lists no device with a telemetry key, so no parameter can be explored."
        />
      </div>
    );
  }

  const scopeText = `${activeDevice.label}${activeDevice.model ? ` · ${activeDevice.model}` : ""} · ${activeGroup.classLabel.toLowerCase()} · ${tiles.length} of ${MAX_PARAMS} series`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader title="Explorer" scope={scopeText} showTimeControl={false} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(200px, 240px) minmax(0, 1fr)",
          gap: 14,
          minHeight: 0,
        }}
      >
      {/* Device tree sidebar */}
      <aside
        className="card"
        style={{
          padding: 0,
          overflow: "auto",
          maxHeight: "calc(100vh - 170px)",
        }}
      >
        <div style={{ padding: "10px 12px 6px" }}>
          <input
            type="search"
            className="search"
            placeholder="Find device or parameter"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%", maxWidth: "none" }}
          />
        </div>
        {groups.map((group) => (
          <DeviceTreeGroup
            key={group.key}
            group={group}
            selectedDevice={activeDevice.id}
            pickedKeys={pickedKeys}
            searchQuery={searchQuery}
            // Focus only moves which group's parameters are listed; it does NOT
            // clear the cross-class picks, so an irradiance series stays on the
            // chart while the viewer focuses an inverter to add power (2.2).
            onSelectDevice={(id) => setSelectedDevice(id)}
            onToggleParam={togglePick}
          />
        ))}
      </aside>

      {/* Toolbar + content */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          minWidth: 0,
        }}
      >
        <Toolbar
          resolution={resolution}
          onResolution={setResolution}
          compareMode={compareMode}
          onCompareMode={setCompareMode}
          viewStyle={viewStyle}
          onViewStyle={setViewStyle}
          displayMode={displayMode}
          onDisplayMode={setDisplayMode}
          onCopyLink={handleCopyLink}
          onExportCsv={handleExportCsv}
          note={exportNote}
        />

        {displayMode === "table" ? (
          <DataTableView tiles={tiles} site={site} resolution={resolution} />
        ) : viewStyle === "overlay_normalised" ? (
          <NormalisedOverlay
            tiles={tiles}
            site={site}
            resolution={resolution}
          />
        ) : viewStyle === "overlay_multi_axis" ? (
          <MultiAxisOverlay
            tiles={tiles}
            site={site}
            resolution={resolution}
          />
        ) : (
          <StackView tiles={tiles} site={site} resolution={resolution} />
        )}
      </div>
      </div>
    </div>
  );
}

/* ── Toolbar ────────────────────────────────────────────────────────────── */

function Toolbar({
  resolution,
  onResolution,
  compareMode,
  onCompareMode,
  viewStyle,
  onViewStyle,
  displayMode,
  onDisplayMode,
  onCopyLink,
  onExportCsv,
  note,
}: {
  resolution: Resolution;
  onResolution: (r: Resolution) => void;
  compareMode: CompareMode;
  onCompareMode: (m: CompareMode) => void;
  viewStyle: ViewStyle;
  onViewStyle: (v: ViewStyle) => void;
  displayMode: DisplayMode;
  onDisplayMode: (d: DisplayMode) => void;
  onCopyLink: () => void;
  onExportCsv: () => void;
  note: string | null;
}): ReactNode {
  return (
    <div
      className="card"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <ToolbarRow label="Resolution">
        <div className="chips">
          {RESOLUTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`chip${resolution === r.id ? " on" : ""}`}
              onClick={() => onResolution(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </ToolbarRow>

      <ToolbarRow label="Compare">
        <div className="chips">
          <button
            type="button"
            className={`chip${compareMode === "one_device_many_params" ? " on" : ""}`}
            onClick={() => onCompareMode("one_device_many_params")}
          >
            one device, many parameters
          </button>
          <button
            type="button"
            className={`chip${compareMode === "one_param_many_devices" ? " on" : ""}`}
            onClick={() => onCompareMode("one_param_many_devices")}
          >
            one parameter, many devices
          </button>
        </div>
      </ToolbarRow>

      <ToolbarRow label="View">
        <div className="chips">
          <button
            type="button"
            className={`chip${viewStyle === "stack" ? " on" : ""}`}
            onClick={() => onViewStyle("stack")}
          >
            Stack
          </button>
          <button
            type="button"
            className={`chip${viewStyle === "overlay_normalised" ? " on" : ""}`}
            onClick={() => onViewStyle("overlay_normalised")}
          >
            Overlay, normalised
          </button>
          <button
            type="button"
            className={`chip${viewStyle === "overlay_multi_axis" ? " on" : ""}`}
            onClick={() => onViewStyle("overlay_multi_axis")}
          >
            Overlay, multi-axis
          </button>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
          {note ? (
            <span className="muted" style={{ fontSize: 11 }}>{note}</span>
          ) : null}
          <div className="chips">
            <button
              type="button"
              className={`chip${displayMode === "chart" ? " on" : ""}`}
              onClick={() => onDisplayMode("chart")}
            >
              Chart
            </button>
            <button
              type="button"
              className={`chip${displayMode === "table" ? " on" : ""}`}
              onClick={() => onDisplayMode("table")}
            >
              Table
            </button>
          </div>
          <button type="button" className="btn small" onClick={onCopyLink}>
            Copy link
          </button>
          <button type="button" className="btn small" onClick={onExportCsv}>
            Export CSV
          </button>
        </div>
      </ToolbarRow>
    </div>
  );
}

function ToolbarRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      <span
        className="muted"
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          minWidth: 70,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/* ── Stack view (default) ──────────────────────────────────────────────── */

function StackView({
  tiles,
  site,
  resolution,
}: {
  tiles: { device: string; param: DeviceParam }[];
  site: string;
  resolution: Resolution;
}): ReactNode {
  if (tiles.length === 0) {
    return (
      <EmptyExplorer />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {tiles.map((t) => (
        <StackTile
          key={`${t.device}:${t.param.signal}`}
          site={site}
          device={t.device}
          param={t.param}
          resolution={resolution}
        />
      ))}
    </div>
  );
}

function StackTile({
  site,
  device,
  param,
  resolution,
}: {
  site: string;
  device: string;
  param: DeviceParam;
  resolution: Resolution;
}): ReactNode {
  const query = useGetV1Series(seriesParams(site, device, param.signal, resolution));

  const label = `${param.label} · ${device}`;

  if (query.isLoading) {
    return (
      <div className="card">
        <h4>
          {label} <span className="sub">{param.unit}</span>
        </h4>
        <Skeleton variant="tile" />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <div className="card">
        <h4>
          {label} <span className="sub">{param.unit}</span>
        </h4>
        <span className="muted">series unavailable</span>
      </div>
    );
  }

  const s = query.data.data.data;
  const points = s.points ?? [];
  const times = points.map((p) => Date.parse(p.ts) / 1000);
  const values = points.map((p) => p.value ?? null);

  if (points.length === 0) {
    return (
      <div className="card">
        <h4>
          {label} <span className="sub">{param.unit}</span>
        </h4>
        <span className="muted">no data</span>
      </div>
    );
  }

  // min/max are seeded from the series itself, never from 0 or Infinity — an
  // all-negative curve must not report "max 0" (that would be a made-up number).
  const nums = values.filter((v): v is number => v !== null);
  const nowVal = nums.at(-1);
  const maxVal = nums.length > 0 ? nums.reduce((a, b) => (b > a ? b : a), nums[0]!) : undefined;
  const minVal = nums.length > 0 ? nums.reduce((a, b) => (b < a ? b : a), nums[0]!) : undefined;

  const header = (
    <h4
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 6,
        margin: 0,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {label}
      <span className="sub">{param.unit}</span>
      <span
        style={{
          marginLeft: "auto",
          fontFamily: "var(--sk-font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontSize: 10,
          fontWeight: 400,
          color: "var(--sk-color-ink-3)",
        }}
      >
        {nums.length === 0
          ? "no numeric points in this window"
          : `now ${nowVal?.toLocaleString() ?? "—"} · max ${maxVal!.toLocaleString()} · min ${minVal!.toLocaleString()}`}
      </span>
    </h4>
  );

  return (
    <TimeSeriesTile
      label={label}
      unit={param.unit}
      header={header}
      times={times}
      values={values}
      xRange={generationWindow(resolution, times)}
      // 150 px canvas -> ~110 px plot, inside the 90-130 px stack-tile band
      // (CHART-SPECIFICATION §8); 120 left only ~70 px (UX-REV-002 2.1).
      height={150}
    />
  );
}

/* ── Timestamp alignment ───────────────────────────────────────────────────
   uPlot takes ONE x array for every series, so overlaid series must be placed
   on a shared timeline. Zipping them onto the first series' timestamps silently
   misplots any series of a different length or offset; the union timeline below
   keeps each point at its own instant and leaves a gap where a series has none. */

function alignOnUnionTimeline(
  series: { times: number[]; values: (number | null)[] }[],
): { times: number[]; columns: (number | null)[][] } {
  const all = new Set<number>();
  for (const s of series) for (const t of s.times) all.add(t);
  const times = [...all].sort((a, b) => a - b);
  const slot = new Map<number, number>(times.map((t, i) => [t, i]));
  const columns = series.map((s) => {
    const column = new Array<number | null>(times.length).fill(null);
    for (let i = 0; i < s.times.length; i++) {
      const j = slot.get(s.times[i]!);
      if (j !== undefined) column[j] = s.values[i] ?? null;
    }
    return column;
  });
  return { times, columns };
}

/* ── Normalised overlay (UX-006 §5.15) ─────────────────────────────────
   All series on one 0-100 % axis, each with a distinct dash pattern.
   First series uses the accent colour, others use measured.             */

function NormalisedOverlay({
  tiles,
  site,
  resolution,
}: {
  tiles: { device: string; param: DeviceParam }[];
  site: string;
  resolution: Resolution;
}): ReactNode {
  const theme = chartTheme();

  const queries = useQueries({
    queries: tiles.map((t) =>
      getGetV1SeriesQueryOptions(seriesParams(site, t.device, t.param.signal, resolution)),
    ),
  });

  if (tiles.length === 0) {
    return (
      <EmptyExplorer />
    );
  }

  const loading = queries.some((q) => q.isLoading);
  if (loading) {
    return (
      <div className="card">
        <Skeleton variant="tile" />
      </div>
    );
  }

  const seriesArrays: {
    label: string;
    times: number[];
    pctValues: (number | null)[];
  }[] = [];

  for (let i = 0; i < tiles.length; i++) {
    const q = queries[i];
    if (!q || q.isError || !q.data || q.data.status !== 200) continue;
    const points = q.data.data.data.points ?? [];
    if (points.length === 0) continue;
    const values = points.map((p) => p.value ?? null);
    const nums = values.filter((v): v is number => v !== null);
    if (nums.length === 0) continue;
    const maxVal = Math.max(...nums);
    const pctValues = values.map((v) =>
      v != null && maxVal > 0 ? (v / maxVal) * 100 : null,
    );
    const times = points.map((p) => Date.parse(p.ts) / 1000);
    const t = tiles[i]!;
    seriesArrays.push({
      label: `${t.device} ${t.param.label}`,
      times,
      pctValues,
    });
  }

  if (seriesArrays.length === 0) {
    return (
      <div className="card">
        <span className="muted">no data for selected parameters</span>
      </div>
    );
  }

  const aligned = alignOnUnionTimeline(
    seriesArrays.map((s) => ({ times: s.times, values: s.pctValues })),
  );
  const alignedData: UPlotChartProps["data"] = [aligned.times, ...aligned.columns];
  const xWindow = generationWindow(resolution, aligned.times);

  const uSeries = [
    {},
    ...seriesArrays.map((s, i) => ({
      label: s.label,
      stroke: LINE_COLORS[i % LINE_COLORS.length]!,
      width: i === 0 ? 2 : 1.5,
      dash: LINE_DASHES[i % LINE_DASHES.length],
      points: { show: false },
    })),
  ];

  const options: UPlotChartProps["options"] = {
    scales: {
      x: { time: true, ...(xWindow ? { range: () => xWindow } : {}) },
      y: { range: [0, 105] },
    },
    legend: { show: false },
    axes: [
      {
        // X axis: tick line only — gridlines are horizontal only (UX-006 §2).
        stroke: theme.axis,
        // 24-hour ticks, like every other time in the product.
        values: timeAxisValues,
        grid: { show: false },
        ticks: { stroke: theme.axis },
      },
      {
        stroke: theme.textMuted,
        grid: { stroke: theme.grid, width: 1 },
        ticks: { show: false },
      },
    ],
    series: uSeries,
  };

  return (
    <div className="card">
      <h4>
        {seriesArrays.length} quantities on one axis{" "}
        <span className="sub">
          percent of each series&rsquo; own maximum over the window
        </span>
      </h4>
      <UPlotChart
        options={options}
        data={alignedData}
        height={220}
        ariaLabel="Normalised overlay"
        summary={`${seriesArrays.length} series normalised to 0-100%`}
      />
      <OverlayLegend
        items={seriesArrays.map((s, i) => ({
          label: s.label,
          colorIdx: i,
        }))}
      />
    </div>
  );
}

/* ── Multi-axis overlay (UX-006 §5.15) ─────────────────────────────────
   Up to 3 series, each on its own Y axis (A left, B+C right).
   "scales differ" chip shown.                                           */

function MultiAxisOverlay({
  tiles,
  site,
  resolution,
}: {
  tiles: { device: string; param: DeviceParam }[];
  site: string;
  resolution: Resolution;
}): ReactNode {
  const theme = chartTheme();
  const limited = tiles.slice(0, 3);

  const queries = useQueries({
    queries: limited.map((t) =>
      getGetV1SeriesQueryOptions(seriesParams(site, t.device, t.param.signal, resolution)),
    ),
  });

  if (limited.length === 0) {
    return (
      <EmptyExplorer hint="max 3 for multi-axis" />
    );
  }

  const loading = queries.some((q) => q.isLoading);
  if (loading) {
    return (
      <div className="card">
        <Skeleton variant="tile" />
      </div>
    );
  }

  const seriesArrays: {
    label: string;
    unit: string;
    times: number[];
    values: (number | null)[];
  }[] = [];

  for (let i = 0; i < limited.length; i++) {
    const q = queries[i];
    if (!q || q.isError || !q.data || q.data.status !== 200) continue;
    const points = q.data.data.data.points ?? [];
    if (points.length === 0) continue;
    const t = limited[i]!;
    seriesArrays.push({
      label: `${t.device} ${t.param.label}`,
      unit: t.param.unit,
      times: points.map((p) => Date.parse(p.ts) / 1000),
      values: points.map((p) => p.value ?? null),
    });
  }

  if (seriesArrays.length === 0) {
    return (
      <div className="card">
        <span className="muted">no data for selected parameters</span>
      </div>
    );
  }

  const aligned = alignOnUnionTimeline(seriesArrays);
  const alignedData: UPlotChartProps["data"] = [aligned.times, ...aligned.columns];
  const xWindow = generationWindow(resolution, aligned.times);

  const scaleKeys = seriesArrays.map((_, i) =>
    i === 0 ? "y" : `y${i + 1}`,
  );

  const yAxes = seriesArrays.map((s, i) => ({
    scale: scaleKeys[i],
    stroke: LINE_COLORS[i % LINE_COLORS.length]!,
    side: i === 0 ? 3 : 1,
    grid: i === 0 ? { stroke: theme.grid, width: 1 } : { show: false },
    ticks: { show: i === 0 },
    label: `${AXIS_LETTERS[i]} · ${s.unit}`,
    labelSize: 14,
    size: 50,
  }));

  const uSeries = [
    {},
    ...seriesArrays.map((s, i) => ({
      label: s.label,
      scale: scaleKeys[i],
      stroke: LINE_COLORS[i % LINE_COLORS.length]!,
      width: 2,
      dash: LINE_DASHES[i % LINE_DASHES.length],
      points: { show: false },
    })),
  ];

  // Each Y axis starts at zero unless a floor is set (UX-006 §5.15: "axes from
  // zero"), so a line can be read against its own scale without a hidden offset.
  const scales: NonNullable<UPlotChartProps["options"]>["scales"] = {
    x: { time: true, ...(xWindow ? { range: () => xWindow } : {}) },
  };
  for (const k of scaleKeys) {
    scales[k] = { range: (_u, _min, max) => [0, max ?? 1] };
  }

  const options: UPlotChartProps["options"] = {
    scales,
    legend: { show: false },
    axes: [
      {
        // X axis: tick line only — gridlines are horizontal only (UX-006 §2).
        stroke: theme.axis,
        // 24-hour ticks, like every other time in the product.
        values: timeAxisValues,
        grid: { show: false },
        ticks: { stroke: theme.axis },
      },
      ...yAxes,
    ],
    series: uSeries,
  };

  return (
    <div className="card">
      <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span>
          {seriesArrays.length === 1
            ? "One axis"
            : `${seriesArrays.length} axes`}
        </span>
        <span className="sub">
          A left{seriesArrays.length > 1 ? " · B, C right" : ""}
        </span>
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
          {seriesArrays.length > 1 && (
            <span className="chip">
              scales differ · {seriesArrays.length} axes
            </span>
          )}
          <span className="chip">axes from zero</span>
        </span>
      </h4>
      <UPlotChart
        options={options}
        data={alignedData}
        height={220}
        ariaLabel="Multi-axis overlay"
        summary={`${seriesArrays.length} series on separate axes`}
      />
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 8,
          fontSize: "0.72rem",
          color: "var(--sk-color-ink-2)",
        }}
      >
        {seriesArrays.map((s, i) => (
          <span
            key={s.label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <svg width={24} height={8}>
              <line
                x1={0}
                y1={4}
                x2={24}
                y2={4}
                stroke={LINE_COLORS[i % LINE_COLORS.length]}
                strokeWidth={1.5}
                strokeDasharray={
                  LINE_DASHES[i % LINE_DASHES.length]?.join(",") ?? "0"
                }
              />
            </svg>
            {AXIS_LETTERS[i]} · {s.label}, {s.unit}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Overlay legend ─────────────────────────────────────────────────── */

function OverlayLegend({
  items,
}: {
  items: { label: string; colorIdx: number }[];
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 8,
        fontSize: "0.72rem",
        color: "var(--sk-color-ink-2)",
      }}
    >
      {items.map((item) => (
        <span
          key={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <svg width={24} height={8}>
            <line
              x1={0}
              y1={4}
              x2={24}
              y2={4}
              stroke={LINE_COLORS[item.colorIdx % LINE_COLORS.length]}
              strokeWidth={1.5}
              strokeDasharray={
                LINE_DASHES[item.colorIdx % LINE_DASHES.length]?.join(",") ??
                "0"
              }
            />
          </svg>
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ── Data table view ───────────────────────────────────────────────────── */

function DataTableView({
  tiles,
  site,
  resolution,
}: {
  tiles: { device: string; param: DeviceParam }[];
  site: string;
  resolution: Resolution;
}): ReactNode {
  if (tiles.length === 0) {
    return (
      <EmptyExplorer />
    );
  }

  return (
    <div className="card" style={{ overflow: "auto" }}>
      {tiles.map((t) => (
        <DataTableTile
          key={`${t.device}:${t.param.signal}`}
          site={site}
          device={t.device}
          param={t.param}
          resolution={resolution}
        />
      ))}
    </div>
  );
}

function DataTableTile({
  site,
  device,
  param,
  resolution,
}: {
  site: string;
  device: string;
  param: DeviceParam;
  resolution: Resolution;
}): ReactNode {
  const query = useGetV1Series(seriesParams(site, device, param.signal, resolution));

  if (query.isLoading) return <Skeleton variant="row" lines={5} />;
  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <div className="muted" style={{ padding: 8 }}>
        {param.label}: unavailable
      </div>
    );
  }

  const points = query.data.data.data.points ?? [];
  if (points.length === 0) {
    return (
      <div className="muted" style={{ padding: 8 }}>
        {param.label}: no data
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
        {param.label} · {device}{" "}
        <span className="muted">{param.unit}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>TIME</th>
            <th className="num">{param.label.toUpperCase()}</th>
            <th>STATE</th>
          </tr>
        </thead>
        <tbody>
          {points
            .slice(-20)
            .reverse()
            .map((p) => (
              <tr key={p.ts}>
                <td
                  style={{
                    fontFamily: "var(--sk-font-mono)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {new Date(p.ts).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </td>
                <td className="num">
                  {p.value != null ? (
                    p.value.toLocaleString()
                  ) : (
                    <span className="muted">no data</span>
                  )}
                </td>
                <td>
                  {/* A point with no state is NOT called "measured" — the
                      endpoint simply did not state one. */}
                  {p.state ? (
                    <span className={`st ${p.state === "MEASURED" ? "ok" : "warn"}`}>
                      <i />
                      {p.state.toLowerCase().replace(/_/g, " ")}
                    </span>
                  ) : (
                    <span className="muted">not stated</span>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {points.length > 20 && (
        <div className="muted" style={{ fontSize: 11, padding: "6px 0" }}>
          {points.length - 20} more rows
        </div>
      )}
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────────── */

function EmptyExplorer({ hint }: { hint?: string } = {}): ReactNode {
  return (
    <div
      className="card"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "48px 24px",
        textAlign: "center",
      }}
    >
      <span style={{ fontSize: 24, opacity: 0.3 }}>&#xf0e8;</span>
      <span style={{ fontWeight: 600, fontSize: 14 }}>No parameters selected</span>
      <span className="muted" style={{ fontSize: 12, maxWidth: 280 }}>
        Pick a device from the tree on the left, then toggle up to {MAX_PARAMS} parameters to plot.
        {hint ? ` (${hint})` : null}
      </span>
    </div>
  );
}

/* ── Device tree sidebar ───────────────────────────────────────────── */

function DeviceTreeGroup({
  group,
  selectedDevice,
  pickedKeys,
  searchQuery,
  onSelectDevice,
  onToggleParam,
}: {
  group: DeviceGroup;
  selectedDevice: string;
  pickedKeys: Set<string>;
  searchQuery: string;
  onSelectDevice: (id: string) => void;
  onToggleParam: (device: string, signal: string) => void;
}): ReactNode {
  const hasSelectedDevice = group.devices.some(
    (d) => d.id === selectedDevice,
  );
  // The device a parameter toggle attaches to for THIS group: the focused device
  // if it belongs here, otherwise the group's first device — so a parameter found
  // by search (e.g. irradiance on a weather station) can be added without first
  // leaving the inverter the viewer is on (UX-REV-002 2.2).
  const repDevice = group.devices.find((d) => d.id === selectedDevice) ?? group.devices[0];
  const query = searchQuery.toLowerCase();

  const filteredDevices = query
    ? group.devices.filter(
        (d) =>
          d.label.toLowerCase().includes(query) ||
          d.id.toLowerCase().includes(query),
      )
    : group.devices;
  const filteredParams = query
    ? group.params.filter(
        (p) =>
          p.label.toLowerCase().includes(query) ||
          p.signal.toLowerCase().includes(query),
      )
    : group.params;

  if (query && filteredDevices.length === 0 && filteredParams.length === 0)
    return null;

  return (
    <details
      open={hasSelectedDevice || (query.length > 0 && (filteredDevices.length > 0 || filteredParams.length > 0))}
      style={{ borderBottom: "1px solid var(--sk-color-border-1)" }}
    >
      <summary
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: "var(--sk-color-ink-3)",
        }}
      >
        <span style={{ flex: 1 }}>{group.classLabel}</span>
        <span>{group.count}</span>
      </summary>

      <div style={{ padding: "0 4px 4px" }}>
        {filteredDevices.map((device) => (
          <TreeItem
            key={device.id}
            label={device.label}
            {...(device.model ? { detail: device.model } : {})}
            selected={selectedDevice === device.id}
            onClick={() => onSelectDevice(device.id)}
            {...(selectedDevice === device.id ? { badge: "selected" } : {})}
          />
        ))}

        {/* Params render when this group's device is focused OR when a search
            matches them — the latter is the path that recovers a cross-class
            signal like irradiance (UX-REV-002 C3: a count with no rows beneath). */}
        {(hasSelectedDevice || (!!query && filteredParams.length > 0)) && repDevice && (
          <div style={{ padding: "6px 0 4px" }}>
            <div
              style={{
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                color: "var(--sk-color-ink-3)",
              }}
            >
              <span style={{ flex: 1 }}>Parameters · {repDevice.label}</span>
              <span>{group.params.length}</span>
            </div>
            {group.params.length === 0 ? (
              <div
                className="muted"
                style={{ padding: "4px 8px", fontSize: 11 }}
              >
                no reported parameters for this group
              </div>
            ) : filteredParams.length === 0 ? (
              <div
                className="muted"
                style={{ padding: "4px 8px", fontSize: 11 }}
              >
                no parameter matches the search
              </div>
            ) : (
              filteredParams.map((param) => (
                <TreeItem
                  key={param.signal}
                  label={param.label}
                  {...(param.unit ? { detail: param.unit } : {})}
                  selected={pickedKeys.has(`${repDevice.id}:${param.signal}`)}
                  onClick={() => onToggleParam(repDevice.id, param.signal)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function TreeItem({
  label,
  detail,
  selected,
  onClick,
  badge,
}: {
  label: string;
  detail?: string;
  selected: boolean;
  onClick: () => void;
  badge?: string;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "5px 8px",
        border: "none",
        borderRadius: 4,
        background: selected
          ? "var(--sk-color-accent-soft)"
          : "transparent",
        color: selected ? "var(--sk-color-accent)" : "var(--sk-color-ink-1)",
        fontSize: 12,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: selected ? "var(--sk-color-accent)" : "transparent",
          border: selected ? "none" : "1.5px solid var(--sk-color-border-2)",
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1 }}>
        {label}
        {detail ? (
          <span className="muted" style={{ marginLeft: 6, fontSize: 10 }}>
            {detail}
          </span>
        ) : null}
      </span>
      {badge && (
        <span className="chip" style={{ fontSize: 9 }}>
          {badge}
        </span>
      )}
    </button>
  );
}
